import React, { useState, useEffect } from 'react';
import { Upload, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { validateImage, convertToWebP } from '../../utils/imageUtils';
import { HeroImage, HeroScreenSize } from '../../types/hero';

interface HeroModalProps {
  isOpen: boolean;
  onClose: () => void;
  image: HeroImage | null;
  screenSize: HeroScreenSize;
  onSave: () => void;
}

const HeroModal: React.FC<HeroModalProps> = ({ isOpen, onClose, image, screenSize, onSave }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (image) {
      setImageUrl(image.image_url);
    } else {
      setImageUrl('');
    }
  }, [image]);

  const uploadImage = async (file: File) => {
    try {
      setUploading(true);
      setError(null);

      // Validate image
      const validationError = validateImage(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      // Convert to WebP
      const webpFile = await convertToWebP(file);
      
      const fileName = `${Math.random()}.webp`;
      const filePath = `hero/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('gallery')
        .upload(filePath, webpFile);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('gallery')
        .getPublicUrl(filePath);

      setImageUrl(data.publicUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!imageUrl) {
      setError('Please upload an image');
      return;
    }

    try {
      const data = {
        image_url: imageUrl,
        screen_size: screenSize
      };

      if (image) {
        const { error } = await supabase
          .from('hero_images')
          .update(data)
          .eq('id', image.id);
        if (error) throw error;
      } else {
        // Get the highest display order
        const { data: existingImages, error: orderError } = await supabase
          .from('hero_images')
          .select('display_order')
          .eq('screen_size', screenSize)
          .order('display_order', { ascending: false })
          .limit(1);

        if (orderError) throw orderError;

        const nextOrder = existingImages && existingImages.length > 0
          ? existingImages[0].display_order + 1
          : 0;

        const { error } = await supabase
          .from('hero_images')
          .insert([{ ...data, display_order: nextOrder }]);
        if (error) throw error;
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving image:', err);
      setError('Failed to save image');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-[#1F2B1F] rounded-xl w-full max-w-2xl">
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h2 className="text-xl font-semibold">
            {image ? 'Edit Image' : 'Add New Image'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Image
            </label>
            {imageUrl ? (
              <div className="relative aspect-video">
                <img
                  src={imageUrl}
                  alt="Preview"
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => setImageUrl('')}
                  className="absolute top-2 right-2 p-1 bg-red-500 rounded-full"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            ) : (
              <label className="relative aspect-video cursor-pointer block">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadImage(file);
                  }}
                  disabled={uploading}
                  className="hidden"
                />
                <div className="absolute inset-0 border-2 border-dashed border-white/20 rounded-lg flex items-center justify-center hover:border-white/40 transition-colors">
                  <div className="text-center">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-white/40" />
                    <span className="text-sm text-white/40">
                      {uploading ? 'Uploading...' : 'Click to upload'}
                    </span>
                  </div>
                </div>
              </label>
            )}
          </div>

          {error && (
            <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="px-6 py-2 bg-[#E85C0D] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : image ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HeroModal;